import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, FileText, Calendar, Users, Printer, Send, Check, ChevronRight, FileSpreadsheet, FilePlus } from 'lucide-react';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType } from 'docx';
import { useApp } from '../contexts/AppContext';

const ExportScreen = () => {
  const { events, contractors, getEventAssignments, importedForms, activeForm } = useApp();
  const [exportType, setExportType] = useState('master');
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exporting, setExporting] = useState(false);

  const activeFormData = importedForms.find(f => f.id === activeForm);
  const formEvents = events.filter(e => e.formId === activeForm);

  const exportOptions = [
    {
      id: 'master',
      title: 'Master Schedule',
      description: 'Complete schedule with all events and assignments',
      icon: Calendar,
      color: 'ops-accent-primary'
    },
    {
      id: 'contractor',
      title: 'Contractor Schedule',
      description: 'Individual schedule for a specific contractor',
      icon: Users,
      color: 'ops-accent-secondary'
    },
    {
      id: 'event',
      title: 'Event Roster',
      description: 'Crew list for specific events',
      icon: FileText,
      color: 'ops-accent-info'
    }
  ];

  const formatOptions = [
    { id: 'xlsx', label: 'Excel (.xlsx)', icon: FileSpreadsheet },
    { id: 'csv', label: 'CSV (.csv)', icon: FileText },
    { id: 'docx', label: 'Word (.docx)', icon: FilePlus },
  ];

  const handleSelectAllEvents = () => {
    if (selectedEvents.length === formEvents.length) {
      setSelectedEvents([]);
    } else {
      setSelectedEvents(formEvents.map(e => e.id));
    }
  };

  const toggleEventSelection = (eventId) => {
    if (selectedEvents.includes(eventId)) {
      setSelectedEvents(selectedEvents.filter(id => id !== eventId));
    } else {
      setSelectedEvents([...selectedEvents, eventId]);
    }
  };

  const generateMasterSchedule = () => {
    const data = [];
    formEvents.forEach(event => {
      const assignments = getEventAssignments(event.id);
      if (assignments.length > 0) {
        data.push({
          Event: event.name,
          Date: event.date,
          Location: event.location || 'TBD',
          'Total Crew': assignments.length
        });
        
        assignments.forEach(assignment => {
          data.push({
            Event: '',
            Date: '',
            Location: assignment.position,
            'Total Crew': assignment.contractor?.name || 'Unknown'
          });
        });
        
        data.push({}); // Empty row for spacing
      }
    });
    return data;
  };

  const generateContractorSchedule = (contractorId) => {
    const contractor = contractors.find(c => c.id === contractorId);
    if (!contractor) return [];

    const data = [];
    formEvents.forEach(event => {
      const assignments = getEventAssignments(event.id);
      const assignment = assignments.find(a => a.contractorId === contractorId);
      
      if (assignment) {
        data.push({
          Event: event.name,
          Date: event.date,
          Position: assignment.position,
          Status: 'Confirmed'
        });
      }
    });
    
    return data;
  };

  const generateEventRoster = (eventIds) => {
    const data = [];
    eventIds.forEach(eventId => {
      const event = events.find(e => e.id === eventId);
      if (event) {
        const assignments = getEventAssignments(eventId);
        data.push({
          Event: event.name,
          Date: event.date,
          Name: '',
          Position: '',
          Contact: ''
        });
        
        assignments.forEach(assignment => {
          data.push({
            Event: '',
            Date: '',
            Name: assignment.contractor?.name || 'Unknown',
            Position: assignment.position,
            Contact: assignment.contractor?.email || ''
          });
        });
        
        data.push({}); // Empty row for spacing
      }
    });
    return data;
  };

  const exportToExcel = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Schedule');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${filename}.xlsx`);
  };

  const exportToCSV = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `${filename}.csv`);
  };

  const exportToWord = async (data, filename) => {
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            text: filename,
            heading: 'Heading1',
            alignment: AlignmentType.CENTER,
          }),
          new Paragraph({
            text: `Generated on ${new Date().toLocaleDateString()}`,
            alignment: AlignmentType.CENTER,
          }),
          new Paragraph({ text: '' }), // Empty line
          ...data.map(row => 
            new Paragraph({
              children: Object.entries(row).map(([key, value]) => 
                new TextRun({ text: `${key}: ${value}  `, bold: key === 'Event' })
              )
            })
          )
        ]
      }]
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${filename}.docx`);
  };

  const handleExport = async () => {
    setExporting(true);
    
    try {
      let data = [];
      let filename = '';

      switch (exportType) {
        case 'master':
          data = generateMasterSchedule();
          filename = `Master_Schedule_${new Date().toISOString().split('T')[0]}`;
          break;
        case 'contractor':
          if (selectedContractor) {
            data = generateContractorSchedule(selectedContractor);
            const contractor = contractors.find(c => c.id === selectedContractor);
            filename = `${contractor.name.replace(' ', '_')}_Schedule_${new Date().toISOString().split('T')[0]}`;
          }
          break;
        case 'event':
          data = generateEventRoster(selectedEvents);
          filename = `Event_Roster_${new Date().toISOString().split('T')[0]}`;
          break;
        default:
          break;
      }

      if (data.length > 0) {
        switch (exportFormat) {
          case 'xlsx':
            exportToExcel(data, filename);
            break;
          case 'csv':
            exportToCSV(data, filename);
            break;
          case 'docx':
            await exportToWord(data, filename);
            break;
          default:
            break;
        }
      }
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  if (!activeForm || !activeFormData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center glass rounded-lg p-8 border border-ops-border">
          <Download className="w-16 h-16 text-ops-accent-warning mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-ops-text-primary mb-2">No Data to Export</h3>
          <p className="text-ops-text-secondary mb-4">Please import data and create schedules first</p>
          <a href="#/import" className="inline-block px-6 py-2 bg-ops-accent-primary text-ops-bg-primary rounded-lg hover:bg-ops-accent-secondary transition-colors">
            Go to Import
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass rounded-lg p-6 border border-ops-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-ops-text-primary">Export Schedules</h2>
            <p className="text-ops-text-secondary mt-1">Generate and download schedule reports</p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="p-2 bg-ops-bg-card border border-ops-border rounded-lg hover:border-ops-accent-primary transition-colors">
              <Printer className="w-4 h-4 text-ops-text-secondary" />
            </button>
            <button className="p-2 bg-ops-bg-card border border-ops-border rounded-lg hover:border-ops-accent-primary transition-colors">
              <Send className="w-4 h-4 text-ops-text-secondary" />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Export Type Selection */}
        <div className="col-span-4 space-y-4">
          <div className="glass rounded-lg p-4 border border-ops-border">
            <h3 className="text-lg font-semibold text-ops-text-primary mb-4">Export Type</h3>
            <div className="space-y-3">
              {exportOptions.map(option => {
                const Icon = option.icon;
                const isSelected = exportType === option.id;
                
                return (
                  <motion.div
                    key={option.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setExportType(option.id)}
                    className={`
                      p-3 rounded-lg cursor-pointer transition-all border
                      ${isSelected 
                        ? 'bg-ops-bg-hover border-ops-accent-primary' 
                        : 'bg-ops-bg-card border-ops-border hover:border-ops-border-light'
                      }
                    `}
                  >
                    <div className="flex items-start space-x-3">
                      <Icon className={`w-5 h-5 mt-0.5 ${isSelected ? 'text-ops-accent-primary' : 'text-ops-text-secondary'}`} />
                      <div className="flex-1">
                        <h4 className="font-semibold text-ops-text-primary text-sm">{option.title}</h4>
                        <p className="text-xs text-ops-text-tertiary mt-0.5">{option.description}</p>
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-ops-accent-primary mt-0.5" />}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Format Selection */}
          <div className="glass rounded-lg p-4 border border-ops-border">
            <h3 className="text-lg font-semibold text-ops-text-primary mb-4">Export Format</h3>
            <div className="space-y-2">
              {formatOptions.map(format => {
                const Icon = format.icon;
                const isSelected = exportFormat === format.id;
                
                return (
                  <button
                    key={format.id}
                    onClick={() => setExportFormat(format.id)}
                    className={`
                      w-full flex items-center space-x-3 p-2 rounded-lg transition-all
                      ${isSelected 
                        ? 'bg-ops-accent-primary bg-opacity-10 border border-ops-accent-primary' 
                        : 'border border-transparent hover:bg-ops-bg-hover'
                      }
                    `}
                  >
                    <Icon className={`w-4 h-4 ${isSelected ? 'text-ops-accent-primary' : 'text-ops-text-secondary'}`} />
                    <span className={`text-sm ${isSelected ? 'text-ops-accent-primary' : 'text-ops-text-secondary'}`}>
                      {format.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Options Panel */}
        <div className="col-span-8">
          <div className="glass rounded-lg p-6 border border-ops-border">
            {/* Contractor Selection (for contractor export) */}
            {exportType === 'contractor' && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-ops-text-primary mb-4">Select Contractor</h3>
                <select
                  value={selectedContractor || ''}
                  onChange={(e) => setSelectedContractor(e.target.value)}
                  className="w-full px-4 py-2 bg-ops-bg-card border border-ops-border rounded-lg text-ops-text-primary focus:border-ops-accent-primary outline-none"
                >
                  <option value="">Choose a contractor...</option>
                  {contractors.map(contractor => (
                    <option key={contractor.id} value={contractor.id}>
                      {contractor.name} ({contractor.email})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Event Selection (for event export) */}
            {exportType === 'event' && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-ops-text-primary">Select Events</h3>
                  <button
                    onClick={handleSelectAllEvents}
                    className="text-sm text-ops-accent-primary hover:text-ops-accent-secondary transition-colors"
                  >
                    {selectedEvents.length === formEvents.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto custom-scrollbar">
                  {formEvents.map(event => {
                    const isSelected = selectedEvents.includes(event.id);
                    const assignments = getEventAssignments(event.id);
                    
                    return (
                      <motion.div
                        key={event.id}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => toggleEventSelection(event.id)}
                        className={`
                          p-3 rounded-lg cursor-pointer transition-all border
                          ${isSelected 
                            ? 'bg-ops-accent-primary bg-opacity-10 border-ops-accent-primary' 
                            : 'bg-ops-bg-card border-ops-border hover:border-ops-border-light'
                          }
                        `}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-ops-text-primary">{event.name}</h4>
                            <p className="text-xs text-ops-text-tertiary mt-1">{event.date}</p>
                            <p className="text-xs text-ops-text-secondary mt-1">
                              {assignments.length} crew assigned
                            </p>
                          </div>
                          <div className={`
                            w-5 h-5 rounded border-2 flex items-center justify-center
                            ${isSelected ? 'border-ops-accent-primary bg-ops-accent-primary' : 'border-ops-border'}
                          `}>
                            {isSelected && <Check className="w-3 h-3 text-ops-bg-primary" />}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Master Schedule Preview */}
            {exportType === 'master' && (
              <div>
                <h3 className="text-lg font-semibold text-ops-text-primary mb-4">Export Preview</h3>
                <div className="bg-ops-bg-card rounded-lg p-4 border border-ops-border">
                  <p className="text-sm text-ops-text-secondary mb-3">
                    This export will include all scheduled events with assigned crew members.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-ops-text-tertiary">Total Events:</span>
                      <span className="text-ops-text-primary font-semibold">{formEvents.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-ops-text-tertiary">Total Contractors:</span>
                      <span className="text-ops-text-primary font-semibold">{contractors.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-ops-text-tertiary">Total Assignments:</span>
                      <span className="text-ops-text-primary font-semibold">
                        {formEvents.reduce((acc, event) => acc + getEventAssignments(event.id).length, 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Export Button */}
            <div className="mt-6">
              <button
                onClick={handleExport}
                disabled={exporting || (exportType === 'contractor' && !selectedContractor) || (exportType === 'event' && selectedEvents.length === 0)}
                className={`
                  w-full py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center space-x-2
                  ${exporting || (exportType === 'contractor' && !selectedContractor) || (exportType === 'event' && selectedEvents.length === 0)
                    ? 'bg-ops-bg-card text-ops-text-tertiary cursor-not-allowed' 
                    : 'bg-ops-accent-primary text-ops-bg-primary hover:bg-ops-accent-secondary cyber-button'
                  }
                `}
              >
                {exporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-ops-bg-primary border-t-transparent"></div>
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>Export Schedule</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportScreen;
